import { inject, Injectable } from '@angular/core';
import { EventTime } from '../domain/recurrence/event.time';
import { EventTimeService } from './event.time.service';

@Injectable({ providedIn: 'root' })
export class OccurrencePreviewFormatter {
  private readonly timeService = inject(EventTimeService);

  /** "38 Dates · Apr 21 – May 28" */
  countAndRangeLabel(occurrences: EventTime[], timezone: string): string {
    if (!occurrences || occurrences.length === 0) return '';
    const first = occurrences[0].startTime;
    const last = occurrences[occurrences.length - 1].startTime;
    const firstLabel = this.timeService.getDayAndMonth(first, timezone);
    const lastLabel = this.timeService.getDayAndMonth(last, timezone);
    const count = occurrences.length;
    return `${count} Date${count === 1 ? '' : 's'} · ${firstLabel} - ${lastLabel}`;
  }

  /** "Mon, Apr 21, 2025 · 7:00 PM – 9:00 PM" */
  occurrenceLabel(time: EventTime, ianaTimezone: string): string {
    const tzOpts: Intl.DateTimeFormatOptions = ianaTimezone ? { timeZone: ianaTimezone } : {};
    const start = new Date(time.startTime);
    const dateStr = start.toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', ...tzOpts,
    });
    if (time.isAllDay) {
      if (time.endTime) {
        const endAllDay = new Date(time.endTime);
        const startDay = start.toLocaleDateString('en-US', { year: 'numeric', month: 'numeric', day: 'numeric', ...tzOpts });
        const endDay   = endAllDay.toLocaleDateString('en-US', { year: 'numeric', month: 'numeric', day: 'numeric', ...tzOpts });
        if (startDay !== endDay) {
          const endDateStr = endAllDay.toLocaleDateString('en-US', {
            weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', ...tzOpts,
          });
          if (time.isEndAllDay) {
            return `${dateStr} - ${endDateStr} · All Day`;
          }
          const endTimeStr = endAllDay.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, ...tzOpts });
          return `${dateStr} All Day - ${endDateStr} · ${endTimeStr}`;
        }
      }
      return `${dateStr} · All Day`;
    }
    const startStr = start.toLocaleTimeString('en-US', {
      hour: 'numeric', minute: '2-digit', hour12: true, ...tzOpts,
    });
    if (!time.endTime) return `${dateStr} · ${startStr}`;
    const end = new Date(time.endTime);
    const endStr = end.toLocaleTimeString('en-US', {
      hour: 'numeric', minute: '2-digit', hour12: true, ...tzOpts,
    });
    const startDay = start.toLocaleDateString('en-US', {
      year: 'numeric', month: 'numeric', day: 'numeric', ...tzOpts,
    });
    const endDay = end.toLocaleDateString('en-US', {
      year: 'numeric', month: 'numeric', day: 'numeric', ...tzOpts,
    });
    if (startDay !== endDay) {
      const endDateStr = end.toLocaleDateString('en-US', {
        weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', ...tzOpts,
      });
      return `${dateStr} · ${startStr} - ${endDateStr} · ${endStr}`;
    }
    return `${dateStr} · ${startStr} - ${endStr}`;
  }

  groupOccurrences(
    occurrences: EventTime[],
    chunkSize: number,
    ianaTimezone: string,
  ): { label: string; times: EventTime[]; offset: number }[] {
    const groups: { label: string; times: EventTime[]; offset: number }[] = [];
    const tzOpts: Intl.DateTimeFormatOptions = ianaTimezone ? { timeZone: ianaTimezone } : {};
    for (let i = 0; i < occurrences.length; i += chunkSize) {
      const chunk = occurrences.slice(i, i + chunkSize);
      const first = new Date(chunk[0].startTime);
      const last = new Date(chunk[chunk.length - 1].startTime);
      const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...tzOpts });
      const fmtFull = (d: Date) =>
        d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', ...tzOpts });
      const firstYear = first.toLocaleDateString('en-US', { year: 'numeric', ...tzOpts });
      const lastYear = last.toLocaleDateString('en-US', { year: 'numeric', ...tzOpts });
      groups.push({
        label: firstYear === lastYear
          ? `${fmt(first)} - ${fmt(last)}, ${firstYear}`
          : `${fmtFull(first)} - ${fmtFull(last)}`,
        times: chunk,
        offset: i,
      });
    }
    return groups;
  }
}
